<script setup lang="ts">
  import { wait } from '@borg/utils';
  import { MpLoadingContainer, pushToastMessage } from '@borg/ui';
  import { JobApplicationType } from '@borg/types';

  const ALLOWED_APPLICATION_TYPES: JobApplicationType[] = ['full', 'quick'];

  const { t } = useI18n();
  const route = useRoute();
  const router = useRouter();
  const config = useRuntimeConfig();

  const baseUrl = `https://${config.public.project.domain}`;
  const canonicalUrl = `${baseUrl}${router.currentRoute.value.path.toString()}`;

  definePageMeta({
    showFooter: false,
  });

  const tags = getJobMetaTags();
  useHead({ ...tags.head, link: [{ rel: 'canonical', href: canonicalUrl }] });
  useSeoMeta(tags.seo);

  const jobId = route.params.jobId?.toString();
  const jobApplicationSource = route.query.source?.toString();
  const job = jobRepository.use();
  const isFormDisabled = ref(false);
  const title = computed(() => `${t('JOB_APPLICATION_PAGE.TITLE')} - ${job.value?.title}`);
  const isSending = computed(() => applicationForm.completeStatus === 'inProgress');

  async function checkIfApplicationIsAllowed() {
    const supportsInternalApplication = job.value?.applicationTypes.some((applicationType) =>
      ALLOWED_APPLICATION_TYPES.includes(applicationType.type),
    );

    if (job.value && !job.value.isActive) {
      isFormDisabled.value = true;
      await pushToastMessage({
        type: 'warning',
        title: t('JOB_APPLICATION_PAGE.JOB_EXPIRED_WARNING'),
        subtitle: t('JOB_APPLICATION_PAGE.REDIRECTING_TO_JOB_PAGE_MESSAGE'),
      });
      logger.warn('Apply page opened on a job ad with isActive=false, redirecting');
      navigateToJob(job.value.id, job.value.slug, { source: 'apply_expired' }, { replace: true });
    }

    if (job.value && !supportsInternalApplication) {
      isFormDisabled.value = true;
      await pushToastMessage({
        type: 'warning',
        title: t('JOB_APPLICATION_PAGE.APPLICATION_TYPE_UNSUPPORTED_WARNING'),
        subtitle: t('JOB_APPLICATION_PAGE.REDIRECTING_TO_JOB_PAGE_MESSAGE'),
      });
      logger.warn(
        'Apply page opened on a job ad where this type of application is not supported, redirecting',
      );
      navigateToJob(
        job.value.id,
        job.value.slug,
        { source: 'apply_unsupported_application_type' },
        { replace: true },
      );
    }
  }

  useHead({
    title: title,
    meta: [{ name: 'description', content: t('JOB_APPLICATION_PAGE.DESCRIPTION') }],
  });

  useSeoMeta({
    ogTitle: title,
    ogDescription: t('JOB_APPLICATION_PAGE.DESCRIPTION'),
  });

  const { $gtag } = useNuxtApp();
  const userInfo = useUserInfo();

  // Provide apply form after job application is loaded, so that we can use the job application data
  const applicationForm = _applicationForm.provide();
  applicationForm.startApplication(jobId, jobApplicationSource);

  async function complete(code?: string) {
    await Promise.all([applicationForm.complete(code), wait(550)]);
    if (applicationForm.completeStatus === 'isSuccess') {
      $gtag?.logJob('job-apply', 'conversion');
      userInfo.setApplied(jobId);
      if (applicationForm.applicationActions?.shouldRegister) {
        await applicationForm.goTo('success-confirm-account');
      } else if (applicationForm.applicationActions?.shouldSaveSearch) {
        await applicationForm.goTo('success-save-search');
      } else {
        await applicationForm.goTo('success-recommended-jobs');
      }
    }
  }

  async function nextView(code?: string) {
    if (applicationForm.viewState === 'form') {
      if (applicationForm.hasQuestionnaire) {
        applicationForm.goTo('questionnaire');
      } else {
        applicationForm.goTo('overview');
      }
    } else if (applicationForm.viewState === 'questionnaire') {
      applicationForm.goTo('overview');
    } else if (applicationForm.viewState === 'overview') {
      if (applicationForm.needsVerification) {
        await applicationForm.startVerification();
        if (applicationForm.startVerificationStatus === 'isSuccess') {
          applicationForm.goTo('verify');
        }
      } else {
        complete();
      }
    } else if (applicationForm.viewState === 'verify' && code) {
      complete(code);
    }
  }

  function prevView() {
    if (applicationForm.viewState === 'form') {
      job.value &&
        navigateToJob(
          job.value.id,
          job.value.slug,
          { source: 'apply_page_back_button' },
          { replace: true },
        );
    } else if (applicationForm.viewState === 'questionnaire') {
      applicationForm.goTo('form');
    } else if (applicationForm.viewState === 'overview') {
      if (applicationForm.hasQuestionnaire) {
        applicationForm.goTo('questionnaire');
      } else {
        applicationForm.goTo('form');
      }
    } else if (applicationForm.viewState === 'verify') {
      applicationForm.goTo('overview');
    }
  }

  async function resendVerificationCode() {
    await applicationForm.resendVerification();
  }

  onMounted(() => {
    checkIfApplicationIsAllowed();
    $gtag?.logJob('job-apply', 'conversionintent');
  });
</script>

<template>
  <MpLoadingContainer
    :is-loading="applicationForm.startApplicationStatus === 'inProgress'"
    :has-error="applicationForm.startApplicationStatus !== 'isSuccess'"
    full-screen
  >
    <WithBackButton
      :visible="!applicationForm.viewState.includes('success')"
      @click="prevView"
    >
      <div class="apply">
        <JobCardNav v-if="!applicationForm.viewState.includes('success')" />
        <ApplyForm
          v-if="applicationForm.viewState === 'form'"
          :disabled="isFormDisabled"
          @next="nextView"
        />
        <ApplyQuestionnaire
          v-else-if="applicationForm.viewState === 'questionnaire'"
          @next="nextView"
        />
        <ApplyVerify
          v-else-if="applicationForm.viewState === 'verify'"
          @resend-verification="resendVerificationCode"
          @next="nextView"
        />
        <ApplyOverview
          v-else-if="applicationForm.viewState === 'overview'"
          @next="nextView"
        />
      </div>
    </WithBackButton>
    <ApplySuccess
      v-if="applicationForm.viewState.includes('success')"
      @next="nextView"
    />
    <ApplySending :is-sending="isSending" />
  </MpLoadingContainer>
</template>

<style scoped lang="scss">
  .apply {
    display: flex;
    flex-direction: column;
    gap: var(--mp-space-60);
    max-width: var(--site-application-form-max-width);
  }
</style>
